import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { TestTube, Loader2, Download, Upload, Trash2, FileText, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';

export default function LabAnalysisPage() {
  const [items, setItems] = useState([]);
  const [reportsByRec, setReportsByRec] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/lab-reports/my-pending');
      setItems(r.data || []);
      // Pre-fetch reports per ciascuno
      const map = {};
      for (const v of r.data || []) {
        try {
          const rep = await api.get(`/haccp-records/${v.id}/lab-reports`);
          map[v.id] = rep.data || [];
        } catch (e) { map[v.id] = []; }
      }
      setReportsByRec(map);
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (recId, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Solo PDF'); return; }
    setUploadingId(recId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/haccp-records/${recId}/lab-reports`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Referto caricato. Il consulente è stato notificato.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
    finally { setUploadingId(null); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare il referto?')) return;
    try { await api.delete(`/lab-reports/${id}`); toast.success('Eliminato'); load(); }
    catch (e) { toast.error('Errore'); }
  };
  const handleDownload = (id) => {
    const token = sessionStorage.getItem('token');
    window.open(`/api/lab-reports/${id}/download?token=${token}`, '_blank');
  };

  return (
    <div className="p-6 space-y-6" data-testid="lab-analysis-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><TestTube className="w-7 h-7 text-purple-500" /> Laboratorio Analisi</h1>
        <p className="text-gray-600 mt-1">Verbali di prelievo HACCP assegnati. Per ogni verbale puoi caricare i referti analitici in PDF.</p>
      </div>

      {loading ? <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        : items.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-gray-500">
            <AlertCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            Nessun verbale assegnato al momento.
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {items.map((v) => {
              const reports = reportsByRec[v.id] || [];
              return (
                <Card key={v.id} className="border-l-4 border-l-purple-500">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
                      <span>{v.titolo || 'Verbale di prelievo'}</span>
                      <div className="flex gap-2">
                        <Badge variant="outline">{v.pdv_name}</Badge>
                        {v.pdv_city && <Badge variant="outline">{v.pdv_city}</Badge>}
                        <Badge>{reports.length} referti</Badge>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div><span className="text-gray-500">Azienda:</span> {v.pdv_company || '—'}</div>
                      <div><span className="text-gray-500">Data prelievo:</span> {v.data_prelievo} {v.ora_prelievo}</div>
                      <div><span className="text-gray-500">Luogo:</span> {v.luogo_prelievo || '—'}</div>
                      <div><span className="text-gray-500">Laboratorio:</span> {v.laboratorio || '—'}</div>
                      <div><span className="text-gray-500">Motivo:</span> {v.motivo || '—'}</div>
                      <div><span className="text-gray-500">Esito visivo:</span> {v.esito_visivo || '—'}</div>
                    </div>

                    {/* Lista campioni */}
                    {(v.campioni || []).length > 0 && (
                      <div className="mt-3">
                        <div className="text-sm font-semibold mb-1">Campioni ({v.campioni.length})</div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border">
                            <thead><tr className="bg-gray-100">
                              <th className="p-1 text-left">#</th><th className="p-1 text-left">Tipo</th>
                              <th className="p-1 text-left">Lotto</th><th className="p-1 text-left">Scadenza</th>
                              <th className="p-1 text-left">Quantità</th><th className="p-1 text-left">Conservazione</th>
                            </tr></thead>
                            <tbody>
                              {v.campioni.map((c, i) => (
                                <tr key={i} className="border-t">
                                  <td className="p-1">{i + 1}</td>
                                  <td className="p-1">{c.tipo_prodotto}</td>
                                  <td className="p-1">{c.lotto}</td>
                                  <td className="p-1">{c.scadenza}</td>
                                  <td className="p-1">{c.quantita}</td>
                                  <td className="p-1">{c.conservazione}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Referti */}
                    <div className="mt-4 border-t pt-3">
                      <div className="flex justify-between items-center mb-2">
                        <div className="text-sm font-semibold">Referti analitici</div>
                        <input type="file" accept=".pdf" ref={(el) => { fileInputs.current[v.id] = el; }} className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(v.id, f); e.target.value = ''; }} />
                        <Button size="sm" onClick={() => fileInputs.current[v.id]?.click()} disabled={uploadingId === v.id} data-testid={`upload-${v.id}`}>
                          {uploadingId === v.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                          Carica referto PDF
                        </Button>
                      </div>
                      {reports.length === 0 ? (
                        <div className="text-xs text-gray-400">Nessun referto caricato.</div>
                      ) : (
                        <div className="space-y-1">
                          {reports.map((r) => (
                            <div key={r.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-blue-500" />
                                <span>{r.filename}</span>
                                <span className="text-xs text-gray-400">· {Math.round((r.size_bytes || 0) / 1024)} KB · {r.uploaded_by_name}</span>
                              </div>
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => handleDownload(r.id)}><Download className="w-4 h-4" /></Button>
                                <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
    </div>
  );
}
